import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { matchesQuery } from "./multiSelectSearch";

export interface MultiSelectOption {
  value: string;
  label: string;
  /** Shown at the right of the row (the drawers' facet counts, 2026-10-26). */
  count?: number;
}

interface Props {
  id: string;
  label: string;
  options: MultiSelectOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  /** Shown on the trigger when nothing is selected. */
  allLabel?: string;
  /** Trigger tooltip while nothing is selected (with a selection it lists the selected labels). */
  hint?: string;
}

/** How many selected labels fit on the trigger before it collapses to "N selected". */
const MAX_INLINE_LABELS = 2;
/** Lists longer than this get a search box; shorter ones stay a plain checkbox list. */
export const SEARCH_THRESHOLD = 8;
/** Mirrors `.ops-ms-panel { max-width }` in styles.css. */
const PANEL_MAX_WIDTH = 320;
const VIEWPORT_MARGIN = 8;

/**
 * A compact multi-select for the slicer bar: a trigger that looks like a select and a popover of
 * checkboxes, with a search box on long lists. Values in one control are OR; controls combine with
 * AND (the API does both).
 */
export function MultiSelect({ id, label, options, selected, onChange, allLabel = "All", hint }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  // Values selected when the panel opened: listed first, frozen while it is open so a row does
  // not jump to the top under the pointer the moment it is ticked.
  const [pinned, setPinned] = useState<string[]>([]);
  // The panel hangs from the trigger's left edge; near the right edge of the viewport it hangs
  // from the right edge instead, so it is never clipped.
  const [alignRight, setAlignRight] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const panelId = useId();
  const countId = useId();

  const close = () => {
    setOpen(false);
    setQuery("");
  };

  // Measured before the panel opens (same render as the open), so it never flashes on the wrong side.
  const toggleOpen = () => {
    if (open) {
      close();
      return;
    }
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) setAlignRight(rect.left + PANEL_MAX_WIDTH + VIEWPORT_MARGIN > window.innerWidth);
    setPinned(selected);
    setOpen(true);
  };

  // A selected value the API no longer lists (no open work right now, or an old URL) stays as a
  // row so it can still be seen and cleared.
  const rows = useMemo(() => {
    const known = new Set(options.map((o) => o.value));
    const stale: MultiSelectOption[] = selected.filter((v) => !known.has(v)).map((v) => ({ value: v, label: v }));
    const all = [...options, ...stale];
    const isPinned = (o: MultiSelectOption) => pinned.includes(o.value);
    return [...all.filter(isPinned), ...all.filter((o) => !isPinned(o))];
  }, [options, selected, pinned]);

  const searchable = rows.length > SEARCH_THRESHOLD;
  const matches = searchable && query ? rows.filter((o) => matchesQuery(o.label, query)) : rows;
  const unselectedMatches = matches.filter((o) => !selected.includes(o.value));

  useEffect(() => {
    if (open && searchable) searchRef.current?.focus();
  }, [open, searchable]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // First Escape clears the search, the next one closes.
      if (query) {
        setQuery("");
        return;
      }
      setOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, query]);

  const labelOf = (value: string) => options.find((o) => o.value === value)?.label ?? value;
  const summary =
    selected.length === 0
      ? allLabel
      : selected.length <= MAX_INLINE_LABELS
        ? selected.map(labelOf).join(", ")
        : `${selected.length} selected`;

  const toggle = (value: string) =>
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);

  const selectMatching = () => onChange([...selected, ...unselectedMatches.map((o) => o.value)]);

  // Enter toggles the only match; with several matches it does nothing rather than guess.
  const onSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const only = matches.length === 1 ? matches[0] : undefined;
    if (query && only) toggle(only.value);
  };

  return (
    <div className="ops-ctl ops-ms" ref={rootRef}>
      <label htmlFor={id}>{label}</label>
      <button
        type="button"
        id={id}
        ref={triggerRef}
        className={`ops-ms-trigger${selected.length ? " has-value" : ""}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        title={selected.length ? selected.map(labelOf).join(", ") : hint}
        onClick={toggleOpen}
      >
        <span className="txt">{summary}</span>
      </button>
      {open && (
        <div id={panelId} role="dialog" aria-label={`${label} filter`} className={`ops-ms-panel${alignRight ? " align-right" : ""}`}>
          {searchable && (
            <div className="ops-ms-search">
              <input
                ref={searchRef}
                type="search"
                aria-label={`Search ${label}`}
                aria-describedby={countId}
                placeholder="Type to search…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onSearchKeyDown}
              />
              <span id={countId} className="ops-ms-count" aria-live="polite">
                {query ? `${matches.length} of ${rows.length}` : ""}
              </span>
            </div>
          )}
          {(selected.length > 0 || (query && unselectedMatches.length > 0)) && (
            <div className="ops-ms-actions">
              {query && unselectedMatches.length > 0 && (
                <button type="button" className="ops-ms-action" onClick={selectMatching}>
                  Select {unselectedMatches.length} matching
                </button>
              )}
              {selected.length > 0 && (
                <button type="button" className="ops-ms-action" onClick={() => onChange([])}>
                  Clear ({selected.length})
                </button>
              )}
            </div>
          )}
          <div className="ops-ms-list">
            {matches.map((o) => (
              <label key={o.value} className="ops-ms-row">
                <input type="checkbox" checked={selected.includes(o.value)} onChange={() => toggle(o.value)} />
                <span>{o.label}</span>
                {o.count !== undefined && <span className="n">{o.count}</span>}
              </label>
            ))}
            {rows.length === 0 && <div className="ops-ms-empty">No values with open work</div>}
            {rows.length > 0 && matches.length === 0 && <div className="ops-ms-empty">No values match “{query.trim()}”</div>}
          </div>
        </div>
      )}
    </div>
  );
}
